/**
 * Cookie utility functions
 */

/**
 * Set a cookie with the specified name, value, and expiry days
 * @param {string} name - Cookie name
 * @param {string} value - Cookie value
 * @param {number} days - Number of days until expiry
 */
export function setCookie(name, value, days) {
    let expires = '';
    if(days) {
        const date = new Date();
        date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
        expires = `;expires=${date.toUTCString()}`;
    }
    document.cookie = `${name}=${value}${expires};path=/`;
    
    // Dispatch custom event for cookie change
    document.dispatchEvent(new CustomEvent('cookieChanged', {
        detail: { name, value }
    }));
}

/**
 * Get a cookie value by name
 * @param {string} name - Cookie name
 * @returns {string|null} - Cookie value or null if not found
 */
export function getCookie(name) {
    const nameEQ = name + "=";
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) === ' ') c = c.substring(1, c.length);
        if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
}

/**
 * Delete a cookie by setting its expiry to the past
 * @param {string} name - Cookie name
 */
export function deleteCookie(name) {
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/`;
    
    // Dispatch custom event for cookie change
    document.dispatchEvent(new CustomEvent('cookieChanged', {
        detail: { name, value: null }
    }));
}

/**
 * Check if a cookie exists
 * @param {string} name - Cookie name
 * @returns {boolean} - True if cookie exists
 */
export function hasCookie(name) {
    return getCookie(name) !== null;
}

/**
 * Add a cookie change listener
 * @param {string} cookieName - Cookie name to listen for
 * @param {function} callback - Callback function to execute when cookie changes
 * @returns {function} - Function to remove the listener
 */
export function addCookieChangeListener(cookieName, callback) {
    const handler = (event) => {
        if (event.detail.name === cookieName) {
            callback(event.detail.value);
        }
    };
    
    document.addEventListener('cookieChanged', handler);
    
    // Return function to remove listener
    return () => {
        document.removeEventListener('cookieChanged', handler);
    };
}

/**
 * Optimized cookie polling with throttling and memory management
 * @param {string} cookieName - Cookie name to monitor
 * @param {function} callback - Callback function to execute when cookie changes
 * @param {number} interval - Polling interval in milliseconds (default: 2000)
 * @returns {function} - Function to stop polling
 */
export function startCookiePolling(cookieName, callback, interval = 2000) {
    let lastValue = getCookie(cookieName);
    let isActive = true;
    
    // Use requestAnimationFrame for better performance
    const poll = () => {
        if (!isActive) return;
        
        const currentValue = getCookie(cookieName);
        
        // Only call callback if value actually changed
        if (currentValue !== lastValue) {
            lastValue = currentValue;
            callback(currentValue);
        }
        
        // Schedule next poll
        setTimeout(poll, interval);
    };
    
    // Start polling
    setTimeout(poll, interval);
    
    // Return function to stop polling
    return () => {
        isActive = false;
    };
}

/**
 * Start cookie polling with visibility API optimization
 * Only polls when the page is visible to save resources
 * @param {string} cookieName - Cookie name to monitor
 * @param {function} callback - Callback function to execute when cookie changes
 * @param {number} interval - Polling interval in milliseconds (default: 3000)
 * @returns {function} - Function to stop polling
 */
export function startOptimizedCookiePolling(cookieName, callback, interval = 3000) {
    let lastValue = getCookie(cookieName);
    let isActive = true;
    let pollTimeout = null;
    
    const poll = () => {
        if (!isActive || document.hidden) {
            // If page is hidden, poll less frequently
            pollTimeout = setTimeout(poll, interval * 3);
            return;
        }
        
        const currentValue = getCookie(cookieName);
        
        // Only call callback if value actually changed
        if (currentValue !== lastValue) {
            lastValue = currentValue;
            callback(currentValue);
        }
        
        // Schedule next poll
        pollTimeout = setTimeout(poll, interval);
    };
    
    // Start polling
    pollTimeout = setTimeout(poll, interval);
    
    // Return function to stop polling
    return () => {
        isActive = false;
        if (pollTimeout) {
            clearTimeout(pollTimeout);
        }
    };
}

/**
 * Debounced cookie polling - reduces frequency of checks
 * @param {string} cookieName - Cookie name to monitor
 * @param {function} callback - Callback function to execute when cookie changes
 * @param {number} interval - Polling interval in milliseconds (default: 5000)
 * @returns {function} - Function to stop polling
 */
export function startDebouncedCookiePolling(cookieName, callback, interval = 5000) {
    let lastValue = getCookie(cookieName);
    let isActive = true;
    let debounceTimer = null;
    
    const poll = () => {
        if (!isActive) return;
        
        const currentValue = getCookie(cookieName);
        
        // Only call callback if value actually changed
        if (currentValue !== lastValue) {
            lastValue = currentValue;
            
            // Debounce the callback to prevent rapid successive calls
            if (debounceTimer) {
                clearTimeout(debounceTimer);
            }
            
            debounceTimer = setTimeout(() => {
                callback(currentValue);
            }, 100);
        }
        
        // Schedule next poll
        setTimeout(poll, interval);
    };
    
    // Start polling
    setTimeout(poll, interval);
    
    // Return function to stop polling
    return () => {
        isActive = false;
        if (debounceTimer) {
            clearTimeout(debounceTimer);
        }
    };
} 